"use client";

import Image from "next/image";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export type Slide =
  | {
      type: "image";
      src: string;
      alt: string;
      /**
       * "cover" (default) fills the 16:9 frame and crops; "contain" shows the whole image on
       * the frame's background, for a screenshot that is not 16:9 and would lose content.
       */
      fit?: "cover" | "contain";
      /**
       * Fill colour behind a `fit: "contain"` slide. Set it to the screenshot's own edge
       * colour and the image appears to fill the whole frame, with no visible letterbox.
       */
      background?: string;
    }
  | { type: "video"; src: string; label: string; poster?: string };

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(onChange: () => void) {
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

type MediaSlideshowProps = {
  slides: readonly Slide[];
  /** Accessible name for the carousel as a whole. */
  label: string;
  /** How long an image stays up before advancing. Videos advance when they end. */
  imageDurationMs?: number;
  /** `sizes` for the image slides; the default suits a slideshow capped at 768px wide. */
  sizes?: string;
  /** Load the first slide eagerly, for a slideshow that is the page's main above-the-fold visual. */
  priority?: boolean;
  /**
   * The frame's background, seen behind `fit: "contain"` slides. A prop rather than a
   * `className` override: two background utilities on one element is decided by CSS source
   * order, not by their order in the string.
   */
  surfaceClassName?: string;
  className?: string;
};

/**
 * Crossfading slideshow of mixed images and videos for the marketing pages.
 *
 * - Images hold for `imageDurationMs`; a video plays through (muted) and advances when it
 *   ends, so a clip is never cut off mid-way.
 * - Auto-advance pauses while the pointer or keyboard focus is inside, and while the
 *   slideshow is off screen — which also stops an off-screen video from playing.
 * - Reduced motion: no auto-advance and no autoplay; videos get native controls instead.
 * - Every slide stays mounted (stacked, only the active one visible) so the crossfade has
 *   both images to blend and a revisited video doesn't reload.
 */
export default function MediaSlideshow({
  slides,
  label,
  imageDurationMs = 5000,
  sizes = "(min-width: 860px) 768px, 100vw",
  priority = false,
  surfaceClassName = "bg-mkt-surface-container",
  className = "",
}: MediaSlideshowProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(false);
  // Server snapshot is `false`: the page prerenders with motion on, and the client
  // corrects it on hydration if the visitor prefers reduced motion.
  const reduceMotion = useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false,
  );

  const count = slides.length;
  const go = useCallback(
    (next: number) => setIndex(((next % count) + count) % count),
    [count],
  );
  const autoplay = !reduceMotion && !hovered && inView && count > 1;

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.25 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Only the active video plays, from the start each time it comes round; the rest pause.
  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === index && inView && !reduceMotion) {
        video.currentTime = 0;
        video.muted = true;
        void video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [index, inView, reduceMotion]);

  // Images advance on a timer; videos advance from their own `ended` handler below.
  useEffect(() => {
    if (!autoplay || slides[index]?.type !== "image") return;
    const timer = window.setTimeout(() => go(index + 1), imageDurationMs);
    return () => window.clearTimeout(timer);
  }, [autoplay, index, slides, imageDurationMs, go]);

  if (count === 0) return null;

  return (
    <div
      ref={rootRef}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      className={`group/slides relative aspect-video overflow-hidden rounded-xl ${surfaceClassName} ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setHovered(false);
      }}
    >
      {slides.map((slide, i) => {
        const active = i === index;
        return (
          <div
            key={slide.src}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${count}`}
            aria-hidden={!active}
            className={`absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none ${
              active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            style={
              slide.type === "image" &&
              slide.fit === "contain" &&
              slide.background
                ? { backgroundColor: slide.background }
                : undefined
            }
          >
            {slide.type === "image" ? (
              <Image
                src={slide.src}
                alt={slide.alt}
                fill
                sizes={sizes}
                priority={priority && i === 0}
                className={
                  slide.fit === "contain" ? "object-contain" : "object-cover"
                }
              />
            ) : (
              <video
                ref={(el) => {
                  videoRefs.current[i] = el;
                  // React's `muted` prop is not written into the server-rendered HTML and is
                  // only set as a property after mount, so set it here too: the clips must
                  // never play with sound, whatever their audio track.
                  if (el) {
                    el.muted = true;
                    el.defaultMuted = true;
                  }
                }}
                className="h-full w-full object-cover"
                src={slide.src}
                poster={slide.poster}
                aria-label={slide.label}
                muted
                playsInline
                preload="metadata"
                controls={reduceMotion}
                onEnded={() => {
                  if (autoplay) go(i + 1);
                }}
              />
            )}
          </div>
        );
      })}

      {count > 1 && (
        <>
          {/* Arrows: always visible on touch, fade in on hover/focus with a pointer. */}
          {(
            [
              {
                dir: -1,
                Icon: ChevronLeft,
                label: "Previous slide",
                side: "left-3",
              },
              {
                dir: 1,
                Icon: ChevronRight,
                label: "Next slide",
                side: "right-3",
              },
            ] as const
          ).map(({ dir, Icon, label: buttonLabel, side }) => (
            <button
              key={buttonLabel}
              type="button"
              aria-label={buttonLabel}
              onClick={() => go(index + dir)}
              className={`absolute ${side} top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-mkt-on-surface shadow-md transition-opacity duration-300 hover:bg-white focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atomcamp-logo-green [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/slides:opacity-100`}
            >
              <Icon aria-hidden="true" className="h-5 w-5" />
            </button>
          ))}

          <div className="absolute inset-x-0 bottom-3 flex justify-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.src}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index ? "true" : undefined}
                onClick={() => go(i)}
                className={`h-2.5 rounded-full shadow transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                  i === index
                    ? "w-6 bg-white"
                    : "w-2.5 bg-white/60 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
